import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MinusIcon, PlusIcon, Trash2Icon, CheckIcon } from 'lucide-react';
import { formatPrice } from '../data/smaken';
import { useRequest } from '../CartContext';
import { Reveal } from '../components/Reveal';
import { ArrowMark, Note } from '../components/Marks';
type Step = 1 | 2 | 3;
type DietKey = 'glutenfri' | 'vegansk' | 'laktosfri';
const VAT_RATE = 0.12;

/** Pickup window when the guest hasn't asked for separate times per place. */
const PICKUP_WINDOW_MINUTES = 60;
const DIETS: {
  key: DietKey;
  label: string;
  note: string;
}[] = [{
  key: 'glutenfri',
  label: 'Glutenfri',
  note: 'Bakas och packas separat'
}, {
  key: 'vegansk',
  label: 'Vegansk',
  note: 'Helt växtbaserat, inget mejeri'
}, {
  key: 'laktosfri',
  label: 'Laktosfri',
  note: 'Mejeri byts, smaken behålls'
}];

/** "11.30" + 60 min → "12.30". Returns '' until a time is picked. */
function addMinutes(time: string, minutes: number): string {
  if (!/^\d{2}:\d{2}$/.test(time)) return '';
  const [h, m] = time.split(':').map(Number);
  const end = (h * 60 + m + minutes) % (24 * 60);
  return `${String(Math.floor(end / 60)).padStart(2, '0')}.${String(end % 60).padStart(2, '0')}`;
}
export function RequestPage() {
  const {
    lines,
    setQuantity,
    remove,
    total,
    clear
  } = useRequest();
  const [step, setStep] = useState<Step>(1);
  const [type, setType] = useState<'foretag' | 'privat'>('foretag');
  const [logistics, setLogistics] = useState<'leverans' | 'upphamtning'>('leverans');
  const [frequency, setFrequency] = useState<'engang' | 'lopande'>('engang');
  const [cadence, setCadence] = useState('Varje vecka');
  const [showErrors, setShowErrors] = useState(false);
  const [reference, setReference] = useState<string | null>(null);
  const [diets, setDiets] = useState<Record<DietKey, {
    on: boolean;
    count: string;
  }>>({
    glutenfri: {
      on: false,
      count: ''
    },
    vegansk: {
      on: false,
      count: ''
    },
    laktosfri: {
      on: false,
      count: ''
    }
  });
  const [splitPickup, setSplitPickup] = useState(false);
  const [pickupTimes, setPickupTimes] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    name: '',
    org: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    address: '',
    notes: ''
  });
  const set = (key: keyof typeof form, value: string) => setForm(p => ({
    ...p,
    [key]: value
  }));

  /** Every distinct place in the basket — pickup happens at each of them. */
  const places = useMemo(() => {
    const seen = new Map<string, string>();
    lines.forEach(l => seen.set(l.placeSlug, l.placeName));
    return [...seen].map(([slug, name]) => ({
      slug,
      name
    }));
  }, [lines]);
  const missing = useMemo(() => {
    const required: (keyof typeof form)[] = ['name', 'email', 'date', 'time'];
    if (logistics === 'leverans') required.push('address');
    return required.filter(k => !form[k].trim());
  }, [form, logistics]);
  const activeDiets = DIETS.filter(d => diets[d.key].on);
  const emailValid = /\S+@\S+\.\S+/.test(form.email);
  const canContinue = missing.length === 0 && emailValid;
  const vat = Math.round(total * VAT_RATE);
  const submit = () => {
    setReference(`SMÅ-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`);
    clear();
  };

  /* -------------------------------- Confirmed -------------------------------- */

  if (reference) {
    return <div className="mx-auto max-w-2xl px-5 py-24 text-center md:px-8" data-id="element-1111" data-mp="16noc1353fm2n">
        <Reveal data-id="element-1112" data-mp="1bnazs6ijskpp">
          <CheckIcon className="mx-auto h-12 w-12 text-rod" aria-hidden="true" data-id="element-1113" data-mp="1bn15i6qgzre7" />
          <h1 className="display-xl mt-6 text-4xl uppercase sm:text-6xl" data-id="element-1114" data-mp="1gmduax0wj8jf">Skickat.</h1>
          <p className="mt-4 text-lg leading-relaxed text-black/70" data-id="element-1115" data-mp="1koiobwv9i76l">
            Vi läser förfrågan själva och återkommer med bekräftelse och pris inom 24 timmar.
          </p>
          <div className="mt-8 border-2 border-black px-6 py-5" data-id="element-1116" data-mp="3igd6dklultx6">
            <p className="text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1117" data-mp="3mgn8gm6is06r">Referens</p>
            <p className="display-xl mt-1 text-2xl" data-id="element-1118" data-mp="2r4dyrso022xi">{reference}</p>
          </div>
          <Link to="/catering" className="mt-8 inline-block bg-black px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-rod" data-id="element-1119" data-mp="2n4vemz405vvq">
            Tillbaka till catering
          </Link>
        </Reveal>
      </div>;
  }

  /* ---------------------------------- Empty ---------------------------------- */

  if (lines.length === 0) {
    return <div className="mx-auto max-w-2xl px-5 py-24 text-center md:px-8" data-id="element-1120" data-mp="23e1km025v9yh">
        <h1 className="display-xl text-4xl uppercase sm:text-6xl" data-id="element-1121" data-mp="2i5sik4602zzw">Tom förfrågan.</h1>
        <Note className="mt-4 block" data-id="element-1122" data-mp="2dd96xcqviexq">Börja med att lägga till något gott.</Note>
        <Link to="/catering" className="mt-8 inline-block bg-rod px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-1123" data-mp="1po6f5c2iz5pi">
          Se all catering
        </Link>
      </div>;
  }
  const inputClass = (invalid: boolean) => `w-full border-b bg-transparent py-3 font-display text-xl outline-none transition-colors duration-150 ease-out placeholder:text-black/25 focus:border-rod sm:text-2xl ${invalid ? 'border-rod' : 'border-black/25'}`;
  const labelClass = 'text-[11px] font-bold uppercase tracking-wide2 text-black/45';
  const choice = (active: boolean) => `border-2 p-6 text-left transition-colors duration-150 ease-out ${active ? 'border-black bg-black text-papper' : 'border-black/20 hover:border-black'}`;
  const steps = [{
    id: 1 as Step,
    label: 'Upplägg'
  }, {
    id: 2 as Step,
    label: 'Uppgifter'
  }, {
    id: 3 as Step,
    label: 'Granska'
  }];
  return <div className="mx-auto max-w-page px-5 py-12 md:px-8 md:py-16" data-id="element-1124" data-mp="2und3qdod3j9t">
      <Reveal data-id="element-1125" data-mp="2zmps52uvsyei">
        <h1 className="display-xl text-5xl uppercase sm:text-7xl" data-id="element-1126" data-mp="2zn8ydmb65ycp">Din förfrågan</h1>
        <p className="mt-3 text-lg text-black/60" data-id="element-1127" data-mp="156hish7yv1hk">
          Tre steg. Vi svarar med bekräftelse och pris inom 24 timmar.
        </p>

        <ol className="mt-10 flex items-center gap-4 border-b-2 border-black pb-5" data-id="element-1128" data-mp="108fqd5onhptf">
          {steps.map((s, i) => <li key={s.id} className="flex items-center gap-4" data-id="element-1129" data-mp="vf4uu0pagvgt" data-mpi={i}>
              <button onClick={() => s.id < step && setStep(s.id)} disabled={s.id > step} className={`flex items-baseline gap-2.5 text-[12px] font-bold uppercase tracking-wide2 ${s.id <= step ? 'text-black' : 'text-black/30'}`} data-id="element-1130" data-mp="rfw0atmfctk7">
                <span className={`display-xl text-2xl ${s.id <= step ? 'text-rod' : 'text-black/25'}`} data-id="element-1131" data-mp="cic0000h3mkd">
                  0{s.id}
                </span>
                <span className="hidden sm:inline" data-id="element-1132" data-mp="26vkzcyzb1row">{s.label}</span>
              </button>
              {i < steps.length - 1 && <span className="h-px w-6 bg-black/25 sm:w-10" data-id="element-1133" data-mp="22tpyfvladgbn" />}
            </li>)}
        </ol>
      </Reveal>

      <div className="mt-12 grid gap-14 lg:grid-cols-[minmax(0,1.3fr),minmax(0,1fr)] lg:gap-20" data-id="element-1134" data-mp="3kqtbwq9mtrzn">
        <div data-id="element-1135" data-mp="3ppxpru7i4dwb">
          {step === 1 && <Reveal className="space-y-10" data-id="element-1136" data-mp="3pomivlireq0z">
              <section data-id="element-1137" data-mp="3uhfqqi9iyeha">
                <h2 className={labelClass} data-id="element-1138" data-mp="3ui7jllbqly3v">Vem beställer?</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-1139" data-mp="2eev23e87tl3">
                  <button onClick={() => setType('foretag')} className={choice(type === 'foretag')} data-id="element-1140" data-mp="2fpu5pwq8d5v">
                    <p className="font-display text-2xl uppercase" data-id="element-1141" data-mp="31vczipiu10qx">Företag</p>
                    <p className="mt-1 text-sm opacity-70" data-id="element-1142" data-mp="36sd8p9azajm9">Kontor, konferens, kickoff</p>
                  </button>
                  <button onClick={() => setType('privat')} className={choice(type === 'privat')} data-id="element-1143" data-mp="3aw1htwf9dn9m">
                    <p className="font-display text-2xl uppercase" data-id="element-1144" data-mp="3fqf67evi6u4s">Privat</p>
                    <p className="mt-1 text-sm opacity-70" data-id="element-1145" data-mp="q37sk4ahy9ht">Bröllop, fest, middag</p>
                  </button>
                </div>
              </section>

              <section data-id="element-1146" data-mp="6pgzete4gz7d">
                <h2 className={labelClass} data-id="element-1147" data-mp="6qs7h5elot4a">Hur vill ni ha det?</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-1148" data-mp="1rfjghbztg04">
                  <button onClick={() => setLogistics('leverans')} className={choice(logistics === 'leverans')} data-id="element-1149" data-mp="1ot4hwmrvy7v">
                    <p className="font-display text-2xl uppercase" data-id="element-1150" data-mp="arlvdys1m7x6">Leverans</p>
                    <p className="mt-1 text-sm opacity-70" data-id="element-1151" data-mp="aphhco0fjsj6">Vi kör ut och dukar upp</p>
                  </button>
                  <button onClick={() => setLogistics('upphamtning')} className={choice(logistics === 'upphamtning')} data-id="element-1152" data-mp="pib8x7sz7z47">
                    <p className="font-display text-2xl uppercase" data-id="element-1153" data-mp="ygddtyftzbwr">Upphämtning</p>
                    <p className="mt-1 text-sm opacity-70" data-id="element-1154" data-mp="ug2yhv65f958">Ni hämtar hos aktören</p>
                  </button>
                </div>
              </section>

              <section data-id="element-1155" data-mp="2w77oynwci3nz">
                <h2 className={labelClass} data-id="element-1156" data-mp="2w7zlq21oi7fu">En gång eller löpande?</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-1157" data-mp="2m39f3ivbyf5a">
                  <button onClick={() => setFrequency('engang')} className={choice(frequency === 'engang')} data-id="element-1158" data-mp="2m54170ic8day">
                    <p className="font-display text-2xl uppercase" data-id="element-1159" data-mp="2q6fi8hn5bq7j">Engångs</p>
                    <p className="mt-1 text-sm opacity-70" data-id="element-1160" data-mp="2ccxyavqjsbgz">Ett tillfälle, ett datum</p>
                  </button>
                  <button onClick={() => setFrequency('lopande')} className={choice(frequency === 'lopande')} data-id="element-1161" data-mp="2ha6guxflyhir">
                    <p className="font-display text-2xl uppercase" data-id="element-1162" data-mp="22hd1rd4903em">Löpande</p>
                    <p className="mt-1 text-sm opacity-70" data-id="element-1163" data-mp="27fem01deu8ro">Samma leverans om och om igen</p>
                  </button>
                </div>

                {frequency === 'lopande' && <div className="mt-5 border-l-2 border-rod pl-5" data-id="element-1164" data-mp="1ydyk6i0nvez2">
                    <label htmlFor="r-interval" className={labelClass} data-id="element-1165" data-mp="3ns3g76z92gl6">
                      Hur ofta?
                    </label>
                    <select id="r-interval" value={cadence} onChange={e => setCadence(e.target.value)} className="mt-1 w-full cursor-pointer border-b border-black/25 bg-transparent py-2 font-display text-xl outline-none transition-colors duration-150 ease-out focus:border-rod" data-id="element-1166" data-mp="3sqxym7tb234r">
                      {['Varje vecka', 'Varannan vecka', 'Varje månad', 'Varje kvartal'].map(o => <option key={o} data-id="element-1167" data-mp="dmni6s0c6w6o">{o}</option>)}
                    </select>
                    <p className="mt-2 text-sm leading-relaxed text-black/60" data-id="element-1168" data-mp="ikp4rmwz1dmw">
                      Datumet nedan blir er första leverans. Ni kan pausa eller ändra när ni vill.
                    </p>
                  </div>}
              </section>

              <button onClick={() => setStep(2)} className="group inline-flex items-center gap-3 bg-black px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-rod" data-id="element-1169" data-mp="2pbf17a0r9grv">
                Vidare
                <ArrowMark className="h-3.5 w-8 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-1170" data-mp="2hsxh69v96jkn" />
              </button>
            </Reveal>}

          {step === 2 && <Reveal className="space-y-8" data-id="element-1171" data-mp="2qtvjmnq1mj14">
              <div data-id="element-1172" data-mp="2qqr6h0ulocyb">
                <label htmlFor="r-name" className={labelClass} data-id="element-1173" data-mp="2339ep5bc5evc">
                  {type === 'foretag' ? 'Företagsnamn' : 'Namn'} *
                </label>
                <input id="r-name" value={form.name} onChange={e => set('name', e.target.value)} placeholder={type === 'foretag' ? 'Verket AB' : 'För- och efternamn'} className={inputClass(showErrors && !form.name.trim())} data-id="element-1174" data-mp="1y5req4faq9zh" />
              </div>

              {type === 'foretag' && <div data-id="element-1175" data-mp="1fd5l7qrv2vi2">
                  <label htmlFor="r-org" className={labelClass} data-id="element-1176" data-mp="1fbkopvlzl2hk">
                    Organisationsnummer
                  </label>
                  <input id="r-org" value={form.org} onChange={e => set('org', e.target.value)} placeholder="556123-4567" className={inputClass(false)} data-id="element-1177" data-mp="3kerig09ky744" />
                </div>}

              <div className="grid gap-8 sm:grid-cols-2" data-id="element-1178" data-mp="2whlvekzgz47u">
                <div data-id="element-1179" data-mp="2wj567w2cwn28">
                  <label htmlFor="r-email" className={labelClass} data-id="element-1180" data-mp="31fvngmpwdop8">
                    E-post *
                  </label>
                  <input id="r-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="namn@foretag.se" className={inputClass(showErrors && !emailValid)} data-id="element-1181" data-mp="35hh6yqwxt18e" />
                </div>
                <div data-id="element-1182" data-mp="psflv7bukadm">
                  <label htmlFor="r-phone" className={labelClass} data-id="element-1183" data-mp="zu9p3mnkwlsc">
                    Telefon
                  </label>
                  <input id="r-phone" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="070 123 45 67" className={inputClass(false)} data-id="element-1184" data-mp="uwhp05cs5td2" />
                </div>
                <div data-id="element-1185" data-mp="h5cz3uz7sffx">
                  <label htmlFor="r-date" className={labelClass} data-id="element-1186" data-mp="h1p5kvtrrt17">
                    Datum *
                  </label>
                  <input id="r-date" type="date" value={form.date} onChange={e => set('date', e.target.value)} className={inputClass(showErrors && !form.date.trim())} data-id="element-1187" data-mp="75c9xsenz53t" />
                </div>
                <div data-id="element-1188" data-mp="1xi1qksto695q">
                  <label htmlFor="r-time" className={labelClass} data-id="element-1189" data-mp="1xjmjyf48zm20">
                    {logistics === 'leverans' ? 'Exakt leveranstid' : 'Upphämtning från'} *
                  </label>
                  <input id="r-time" type="time" value={form.time} onChange={e => set('time', e.target.value)} className={inputClass(showErrors && !form.time.trim())} data-id="element-1190" data-mp="29jjjluiy73xd" />
                  <p className="mt-2 text-sm text-black/55" data-id="element-1191" data-mp="1luqjrmz7gbxh">
                    {logistics === 'leverans' ? 'Vi står på plats på minuten. Ange när det ska stå framme.' : 'Startpunkt för er upphämtning.'}
                  </p>
                </div>
              </div>

              {logistics === 'leverans' ? <div data-id="element-1192" data-mp="1gtudj2rdsdow">
                  <label htmlFor="r-address" className={labelClass} data-id="element-1193" data-mp="12we9tz0gyy0u">
                    Leveransadress *
                  </label>
                  <input id="r-address" value={form.address} onChange={e => set('address', e.target.value)} placeholder="Storgatan 1, 352 30 Växjö" className={inputClass(showErrors && !form.address.trim())} data-id="element-1194" data-mp="17u7euoq1dlzj" />
                </div> : <section className="border-l-2 border-rod pl-5" data-id="element-1195" data-mp="t8s4unokn8o6">
                  <h2 className={labelClass} data-id="element-1196" data-mp="tamu2e6vlmy2">Upphämtning</h2>

                  {places.length > 1 ? <>
                      <p className="mt-2 leading-relaxed text-black/70" data-id="element-1197" data-mp="129hwrgapoewm">
                        Er beställning ligger hos {places.length} verksamheter.{' '}
                        {splitPickup ? 'Ange en tid per ställe nedan.' : `Allt står klart att hämta mellan ${form.time.replace(':', '.') || '––.––'} och ${addMinutes(form.time, PICKUP_WINDOW_MINUTES) || '––.––'}.`}
                      </p>

                      <label className="mt-4 flex cursor-pointer items-start gap-3" data-id="element-1198" data-mp="imtmmgof5tci">
                        <input type="checkbox" checked={splitPickup} onChange={e => setSplitPickup(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-rod" data-id="element-1199" data-mp="nl4yewy9ydxd" />
                        <span className="text-[0.95rem] leading-snug" data-id="element-1200" data-mp="11d1nst8k69q2">
                          Jag vill hämta på olika tider
                          <span className="block text-sm text-black/55" data-id="element-1201" data-mp="16atexl1dprt7">
                            Annars gäller spannet på {PICKUP_WINDOW_MINUTES} minuter ovan.
                          </span>
                        </span>
                      </label>

                      {splitPickup && <div className="mt-5 space-y-4 border-t border-black/15 pt-5" data-id="element-1202" data-mp="3d3dqg5tr6suc">
                          {places.map(place => <div key={place.slug} className="flex flex-wrap items-end justify-between gap-4" data-id="element-1203" data-mp="3h60b9ms0bdi1">
                              <label htmlFor={`pickup-${place.slug}`} className="font-display text-lg leading-tight" data-id="element-1204" data-mp="39m99j7zylkqc">
                                {place.name}
                              </label>
                              <input id={`pickup-${place.slug}`} type="time" value={pickupTimes[place.slug] ?? ''} onChange={e => setPickupTimes(p => ({
                    ...p,
                    [place.slug]: e.target.value
                  }))} className="w-32 border-b border-black/25 bg-transparent py-2 font-display text-xl outline-none transition-colors duration-150 ease-out focus:border-rod" data-id="element-1205" data-mp="2vr6t3qxhkcza" />
                            </div>)}
                        </div>}
                    </> : <p className="mt-2 leading-relaxed text-black/70" data-id="element-1206" data-mp="b4cmtpsxrf4n">
                      Ni hämtar hos {places[0]?.name}. Vi bekräftar adress och tid per mejl.
                    </p>}
                </section>}

              <section data-id="element-1207" data-mp="prpelw9c6h3h">
                <h2 className={labelClass} data-id="element-1208" data-mp="prfkpxl67psi">Specialkost</h2>
                <p className="mt-2 text-sm text-black/55" data-id="element-1209" data-mp="pu1rhv08m19o">
                  Bocka i vad som behövs och hur många portioner av beställningen det gäller.
                </p>
                <div className="mt-4 space-y-3" data-id="element-1210" data-mp="g1cqju2bc5sl">
                  {DIETS.map(diet => <div key={diet.key} className="flex flex-wrap items-center justify-between gap-4 border-b border-black/15 pb-3" data-id="element-1211" data-mp="kzeaeh7kc954">
                      <label className="flex cursor-pointer items-start gap-3" data-id="element-1212" data-mp="1diuqk0c5op8i">
                        <input type="checkbox" checked={diets[diet.key].on} onChange={e => setDiets(p => ({
                    ...p,
                    [diet.key]: {
                      on: e.target.checked,
                      count: e.target.checked ? p[diet.key].count : ''
                    }
                  }))} className="mt-1 h-4 w-4 shrink-0 accent-rod" data-id="element-1213" data-mp="2y6netfsh9hgc" />
                        <span className="text-[0.95rem] leading-snug" data-id="element-1214" data-mp="2abkmgxugsvv4">
                          {diet.label}
                          <span className="block text-sm text-black/55" data-id="element-1215" data-mp="2o7enkuozygxo">{diet.note}</span>
                        </span>
                      </label>

                      {diets[diet.key].on && <div className="flex items-center gap-2" data-id="element-1216" data-mp="3ld3fk5h5nla">
                          <input type="number" min="1" value={diets[diet.key].count} onChange={e => setDiets(p => ({
                    ...p,
                    [diet.key]: {
                      ...p[diet.key],
                      count: e.target.value
                    }
                  }))} aria-label={`Antal ${diet.label.toLowerCase()}`} placeholder="0" className="w-20 border-b border-black/25 bg-transparent py-1.5 text-center font-display text-xl outline-none transition-colors duration-150 ease-out placeholder:text-black/25 focus:border-rod" data-id="element-1217" data-mp="3uos1venozbhu" />
                          <span className="text-[11px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-1218" data-mp="c15sqd7q9kwl">
                            av {lines.reduce((s, l) => s + l.quantity, 0)}
                          </span>
                        </div>}
                    </div>)}
                </div>
              </section>

              <div data-id="element-1219" data-mp="19m75oczxbu4d">
                <label htmlFor="r-notes" className={labelClass} data-id="element-1220" data-mp="19knpeme9pj2l">
                  Övriga allergier och önskemål
                </label>
                <textarea id="r-notes" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Två veganer, en glutenallergi…" className={`${inputClass(false)} resize-none`} data-id="element-1221" data-mp="voigisytq0ti" />
              </div>

              {showErrors && !canContinue && <p role="alert" className="text-sm font-bold uppercase tracking-wide2 text-rod" data-id="element-1222" data-mp="ctv51ckax4v0">
                  Fyll i fälten märkta med *
                </p>}

              <div className="flex flex-wrap gap-4" data-id="element-1223" data-mp="2ya6xpicsoqj">
                <button onClick={() => setStep(1)} className="border-2 border-black px-8 py-4 text-[12px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out hover:bg-black hover:text-papper" data-id="element-1224" data-mp="7wuyl0jji03d">
                  Tillbaka
                </button>
                <button onClick={() => canContinue ? (setShowErrors(false), setStep(3)) : setShowErrors(true)} className="group inline-flex items-center gap-3 bg-black px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-rod" data-id="element-1225" data-mp="20u8n7rg2cd4b">
                  Granska
                  <ArrowMark className="h-3.5 w-8 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-1226" data-mp="2jjf73hslfarp" />
                </button>
              </div>
            </Reveal>}

          {step === 3 && <Reveal className="space-y-8" data-id="element-1227" data-mp="185kvpe4q0rib">
              <dl className="border-t-2 border-black" data-id="element-1228" data-mp="135os3fzxr23a">
                {[['Beställare', type === 'foretag' ? 'Företag' : 'Privat'], ['Upplägg', logistics === 'leverans' ? 'Leverans' : 'Upphämtning'], ['Återkommande', frequency === 'lopande' ? cadence : 'Nej, ett tillfälle'], ['Namn', form.name], ['Org.nr', form.org], ['E-post', form.email], ['Telefon', form.phone], ['Datum', form.date], [logistics === 'leverans' ? 'Leveranstid' : 'Upphämtning', logistics === 'leverans' ? form.time.replace(':', '.') : splitPickup && places.length > 1 ? places.map(p => `${p.name} ${(pickupTimes[p.slug] ?? '').replace(':', '.') || '––.––'}`).join(' · ') : `${form.time.replace(':', '.')}–${addMinutes(form.time, PICKUP_WINDOW_MINUTES)}`], ['Adress', logistics === 'leverans' ? form.address : ''], ['Specialkost', activeDiets.map(d => `${d.label} ${diets[d.key].count || '?'} st`).join(' · ')], ['Önskemål', form.notes]].filter(([, v]) => Boolean(v)).map(([label, value]) => <div key={label} className="flex justify-between gap-8 border-b border-black/15 py-3.5" data-id="element-1229" data-mp="13559uq9nsvie">
                      <dt className="text-[11px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-1230" data-mp="z4v67k9cah6p">
                        {label}
                      </dt>
                      <dd className="max-w-[60%] text-right" data-id="element-1231" data-mp="183gjazw1l403">{value}</dd>
                    </div>)}
              </dl>

              <div className="flex flex-wrap gap-4" data-id="element-1232" data-mp="kcb7cvt82vv2">
                <button onClick={() => setStep(2)} className="border-2 border-black px-8 py-4 text-[12px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out hover:bg-black hover:text-papper" data-id="element-1233" data-mp="ffksucx2g8br">
                  Tillbaka
                </button>
                <button onClick={submit} className="group inline-flex items-center gap-3 bg-rod px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-1234" data-mp="2i2rjbd3xjm3r">
                  Skicka förfrågan
                  <ArrowMark className="h-3.5 w-8 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-1235" data-mp="3erfjeufowoow" />
                </button>
              </div>
            </Reveal>}
        </div>

        {/* --------------------------------- Basket -------------------------------- */}
        <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1236" data-mp="1pbg56yx7s92e">
          <div className="border-2 border-black p-6" data-id="element-1237" data-mp="1ucnex3ix3ibp">
            <h2 className="display-xl text-2xl uppercase" data-id="element-1238" data-mp="1ua150x8k0qqb">Vad ni får</h2>

            <ul className="mt-6 border-t border-black/15" data-id="element-1239" data-mp="1a8pwi1lhg3g5">
              {lines.map(line => <li key={line.id} className="border-b border-black/15 py-4" data-id="element-1240" data-mp="169pdahz3eysm">
                  <div className="flex gap-4" data-id="element-1241" data-mp="11gw8xv7jdsh2">
                    <img src={line.image} alt="" className="h-16 w-16 shrink-0 object-cover" data-id="element-1242" data-mp="11fl4dt69ogow" />
                    <div className="min-w-0 flex-1" data-id="element-1243" data-mp="wgiclafas6yv">
                      <p className="font-display text-lg leading-tight" data-id="element-1244" data-mp="rjruyinuh3bc">{line.name}</p>
                      <p className="text-[10px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-1245" data-mp="mjx4uhbw8hrc">
                        {line.placeName}
                      </p>
                    </div>
                    <button onClick={() => remove(line.id)} aria-label={`Ta bort ${line.name}`} className="shrink-0 text-black/35 transition-colors duration-150 ease-out hover:text-rod" data-id="element-1246" data-mp="dk0co5qap0rj">
                      <Trash2Icon className="h-4 w-4" data-id="element-1247" data-mp="27vx8sbzomusx" />
                    </button>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3" data-id="element-1248" data-mp="2kgdpk36f5qo4">
                    <div className="flex items-center border border-black/20" data-id="element-1249" data-mp="2keao8mztgb43">
                      <button onClick={() => setQuantity(line.id, line.quantity - 1)} aria-label="Minska" className="flex h-8 w-8 items-center justify-center transition-colors duration-150 ease-out hover:bg-black hover:text-papper" data-id="element-1250" data-mp="2p7lyky3u7a0r">
                        <MinusIcon className="h-3.5 w-3.5" data-id="element-1251" data-mp="21jchualhdcgh" />
                      </button>
                      <span className="min-w-[3.5rem] text-center text-sm font-semibold" data-id="element-1252" data-mp="26ip5uyo38pkn">
                        {line.quantity} {line.unit === 'per person' ? 'pers' : 'st'}
                      </span>
                      <button onClick={() => setQuantity(line.id, line.quantity + 1)} aria-label="Öka" className="flex h-8 w-8 items-center justify-center transition-colors duration-150 ease-out hover:bg-black hover:text-papper" data-id="element-1253" data-mp="3hy3zptl031bj">
                        <PlusIcon className="h-3.5 w-3.5" data-id="element-1254" data-mp="lj26j9t34ze3" />
                      </button>
                    </div>
                    <span className="font-display text-lg" data-id="element-1255" data-mp="gsbwhj9ps8d2">{formatPrice(line.price * line.quantity)}</span>
                  </div>
                </li>)}
            </ul>

            <div className="mt-5 space-y-1.5 text-sm" data-id="element-1256" data-mp="pl5peji3369e">
              <div className="flex justify-between text-black/55" data-id="element-1257" data-mp="pknq1e04kbx9">
                <span data-id="element-1258" data-mp="14h4zfzor7gh6">Exkl. moms</span>
                <span data-id="element-1259" data-mp="14g3khk4ma9u6">{formatPrice(total)}</span>
              </div>
              <div className="flex justify-between text-black/55" data-id="element-1260" data-mp="zkeht7xawctn">
                <span data-id="element-1261" data-mp="369172y7sw66o">Moms 12 %</span>
                <span data-id="element-1262" data-mp="36a2vs5hqor6v">{formatPrice(vat)}</span>
              </div>
              <div className="flex items-baseline justify-between border-t border-black/15 pt-3" data-id="element-1263" data-mp="31aztgst58z2l">
                <span className="text-[11px] font-bold uppercase tracking-wide2" data-id="element-1264" data-mp="1grw0fqde83ej">Preliminärt</span>
                <span className="display-xl text-2xl" data-id="element-1265" data-mp="1ls0i3jq1f46c">{formatPrice(total + vat)}</span>
              </div>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-black/50" data-id="element-1266" data-mp="171t87se3ith9">
              Detta är en förfrågan, inte en bindande order. Slutpris bekräftas av varje aktör.
            </p>
          </div>
        </aside>
      </div>
    </div>;
}